import * as API from "@/services/API";

const DEFAULT_OG_IMAGE =
  process.env.VUE_APP_API_URL +
  "/storage/CTCfpLUzCrkNJpVD1LKFxe6UbMwLLpEiUqVUDDig.webp";

const storageUrl = (path) =>
  path ? process.env.VUE_APP_API_URL + "/storage/" + path : DEFAULT_OG_IMAGE;

const pickMetaFields = ({
  meta_description,
  meta_keywords,
  meta_title,
  og_image
} = {}) => ({
  meta_description: meta_description || "",
  meta_keywords: meta_keywords || "",
  meta_title: meta_title || "",
  og_image: og_image || DEFAULT_OG_IMAGE
});

const getArtistPageSeo = async () => {
  return API.apiClient.get("/artists-get-fields");
};

const getProductsPageSeo = async () => {
  return API.apiClient.get("/products-get-fields");
};

const factoryMetaData = async (data, default_data) => {
  return {
    meta_title: data.meta_title
      ? data.meta_title
      : default_data.data.meta_title,
    meta_keywords: data.meta_keywords
      ? data.meta_keywords
      : default_data.data.meta_keywords,
    meta_description: data.meta_description
      ? data.meta_description
      : default_data.data.meta_description,
    og_image: data.og_image || DEFAULT_OG_IMAGE
  };
};

const replaceVariable = (meta_data, search, replace) => {
  return {
    meta_title:
      meta_data.meta_title && meta_data.meta_title.length
        ? meta_data.meta_title.replace(search, replace)
        : "",
    meta_keywords:
      meta_data.meta_keywords && meta_data.meta_keywords.length
        ? meta_data.meta_keywords.replace(search, replace)
        : "",
    meta_description:
      meta_data.meta_description && meta_data.meta_description.length
        ? meta_data.meta_description.replace(search, replace)
        : "",
    og_image: meta_data.og_image || DEFAULT_OG_IMAGE
  };
};

const factoryMethod = async (routeName, to = {}) => {
  let service;

  switch (routeName) {
    case "Home": {
      service = await import("@/services/HomepageFieldsService");
      const { data } = await service.default.getField();
      return pickMetaFields(data);
    }
    case "About": {
      service = await import("@/services/AboutUsFieldsService");
      const data = await service.default.getField();
      return pickMetaFields(data);
    }

    case "Categories": {
      service = await import("@/services/CategoriesFieldService.js");
      const data = await service.default.getCategoriesField();
      return pickMetaFields(data);
    }

    case "Contact": {
      service = await import("@/services/ContactUsService");
      const data = await service.default.getField();
      return pickMetaFields(data);
    }
    case "Artist Archive": {
      service = await import("@/services/ArtistsOur");
      const data = await service.default.getArtistsOurField();
      return pickMetaFields(data);
    }
    case "FAQ": {
      service = await import("@/services/FAQFieldsService");
      const { data } = await service.default.getField();
      return pickMetaFields(data);
    }
    case "artist": {
      service = await import("@/services/ArtistDetails");
      if (!to || !to.params || !to.params.artist) {
        return pickMetaFields({});
      }
      const data = await service.default.getArtistInfo(to.params.artist);
      data.og_image = storageUrl(data.profile_image);
      const default_data = await getArtistPageSeo();
      const result = await factoryMetaData(data, default_data);
      return replaceVariable(result, "{user_name}", data.artist_name);
    }
    case "CategoryDetail": {
      if (!to || !to.params || !to.params.slug) {
        return pickMetaFields({});
      }
      service = await import("@/services/CategoriesFieldService.js");
      const categories = await service.default.getCategories();
      const slugify = (name) =>
        name
          .toLowerCase()
          .trim()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/(^-|-$)/g, "");
      const matched = (categories || []).find(
        (c) => slugify(c.name) === to.params.slug
      );
      if (!matched) {
        return pickMetaFields({});
      }
      const name = matched.name;
      return pickMetaFields({
        meta_title: `${name} Art Prints by Maryland Artists | MDArtGalleries`,
        meta_keywords: `${name} art, ${name} prints, ${name} canvas prints, Maryland art, art prints for living room, gift for art lovers`,
        meta_description: `Shop ${name} art prints and canvas prints from Maryland artists. Find the perfect ${name} piece for your home, office, or as a gift for art lovers.`,
        og_image: storageUrl(matched.path)
      });
    }
    case "Order": {
      service = await import("@/services/ImageService");
      if ((!to && !to.query) || !to.query.image_id) {
        return pickMetaFields({});
      }
      const data = await service.default.getImage(to.query.image_id);
      data.og_image = data.preview_image ? data.preview_image.path : null;
      const default_data = await getProductsPageSeo();
      let result = await factoryMetaData(data, default_data);
      result = replaceVariable(result, "{title}", data.name);
      return replaceVariable(result, "{author}", data.artist_name);
    }
    case "ReturnPolicy": {
      service = await import("@/services/ReturnPolicy");
      const data = await service.default.getReturnPolicyField();
      return pickMetaFields(data);
    }
    case "PrivacyPolicy": {
      service = await import("@/services/PrivacyPolicy");
      const data = await service.default.getPrivacyPolicyField();
      return pickMetaFields(data);
    }
    case "ArtCategories": {
      service = await import("@/services/ArtCategories");
      const data = await service.default.getFields();
      return pickMetaFields(data);
    }
    default:
      return {};
  }
};

export default factoryMethod;
